import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal as BsModal } from 'react-bootstrap';
import { logTone, logLabel } from '../clickLog';
import { formatUtcDateTime } from '../dateTime';

function formatValue(value) {
    if (value == null || value === '') return '—';
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value, null, 2);
        } catch {
            return '—';
        }
    }
    return String(value);
}

const ANDROID_GROUPS = [
  {
    title: 'Клік',
    fields: [
      ['clickTime', 'Час (UTC)'],
      ['appId', 'App ID'],
      ['app_version', 'Версія'],
      ['ip', 'IP'],
    ],
  },
  {
    title: 'Пристрій',
    fields: [
      ['deviceID', 'Device ID'],
      ['androidId', 'Android ID'],
      ['adId', 'Ad ID'],
      ['pushToken', 'Push Token'],
    ],
  },
  {
    title: 'Джерело',
    fields: [
      ['naming', 'Naming'],
      ['referer', 'Referer'],
      ['onelink', 'OneLink'],
      ['deeplink', 'DeepLink'],
      ['installreferrer', 'Install Referrer'],
      ['userAgent', 'User Agent'],
    ],
  },
  {
    title: 'Результат',
    fields: [['finalUrl', 'Final URL']],
  },
];

const IOS_GROUPS = [
  {
    title: 'Клік',
    fields: [
      ['clickTime', 'Час (UTC)'],
      ['appId', 'App ID'],
      ['appVersion', 'Версія застосунку'],
      ['ip', 'IP'],
      ['country', 'Країна'],
    ],
  },
  {
    title: 'Пристрій',
    fields: [
      ['deviceID', 'Device ID'],
      ['idfa', 'IDFA'],
      ['idfv', 'IDFV'],
      ['adId', 'ID для Binom'],
      ['deviceName', 'Модель пристрою'],
      ['osVersion', 'Версія iOS'],
      ['pushToken', 'Push Token'],
    ],
  },
  {
    title: 'Джерело',
    fields: [
      ['naming', 'Naming'],
      ['referer', 'Referer'],
      ['deeplink', 'DeepLink'],
      ['userAgent', 'User Agent'],
    ],
  },
  {
    title: 'Результат',
    fields: [['finalUrl', 'Final URL']],
  },
];

function CopyField({ label, value, wide }) {
  const [copied, setCopied] = useState(false);
  const text = formatValue(value);

  const copy = () => {
    if (!navigator.clipboard || text === '—') return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    });
  };

  return (
    <div className={`cd-field${wide ? ' is-wide' : ''}`}>
      <div className="cd-label">
        {label}
        {text !== '—' ? (
          <button type="button" className="cd-copy" onClick={copy}>
            {copied ? 'скопійовано' : 'копіювати'}
          </button>
        ) : null}
      </div>
      <div className="cd-value">{text}</div>
    </div>
  );
}

const REPEAT_FIELDS = [
  ['ip', 'IP'],
  ['adId', 'Ad ID'],
  ['androidId', 'Android ID'],
  ['app_version', 'Версія'],
  ['naming', 'Naming'],
  ['referer', 'Referer'],
  ['onelink', 'OneLink'],
  ['deeplink', 'DeepLink'],
  ['installreferrer', 'Install Referrer'],
  ['pushToken', 'Push Token'],
  ['userAgent', 'User Agent'],
];

const IOS_REPEAT_FIELDS = [
  ['ip', 'IP'],
  ['country', 'Країна'],
  ['idfa', 'IDFA'],
  ['idfv', 'IDFV'],
  ['adId', 'ID для Binom'],
  ['appVersion', 'Версія застосунку'],
  ['osVersion', 'Версія iOS'],
  ['deviceName', 'Модель пристрою'],
  ['naming', 'Naming'],
  ['referer', 'Referer'],
  ['deeplink', 'DeepLink'],
  ['pushToken', 'Push Token'],
  ['userAgent', 'User Agent'],
];

export default function ClickDetailsModal({ show, onHide, click }) {
    const [main, setMain] = useState(null);
    const [repeats, setRepeats] = useState([]);
    const [openRepeat, setOpenRepeat] = useState(null);

    useEffect(() => {
        setMain(null);
        setRepeats([]);
        setOpenRepeat(null);

        if (!show || !click?.deviceID) return undefined;

        let cancelled = false;

        axios
            .get('/api/clicks/repeats', { params: { deviceID: click.deviceID } })
            .then((res) => {
                if (cancelled) return;
                setMain(res.data?.main || null);
                setRepeats(res.data?.items || []);
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [show, click]);

    const data = main || click;
    const isIos = Boolean(data && Object.prototype.hasOwnProperty.call(data, 'idfv'));
    const groups = isIos ? IOS_GROUPS : ANDROID_GROUPS;
    const repeatFields = isIos ? IOS_REPEAT_FIELDS : REPEAT_FIELDS;

    return (
        <BsModal show={show} onHide={onHide} centered size="xl" dialogClassName="cd-modal">
            <BsModal.Header closeButton>
                <BsModal.Title className="cd-title">
                    <span className={`click-log-dot is-${logTone(data?.clickLog)}`} />
                    {logLabel(data?.clickLog)}
                    <em className="cd-subtitle">{data?.appId || ''}</em>
                </BsModal.Title>
            </BsModal.Header>

            <BsModal.Body className="cd-body">
                {!data ? (
                    <div className="settings-empty">Немає даних</div>
                ) : (
                    groups.map((group) => (
                        <section className="cd-group" key={group.title}>
                            <div className="cd-group-title">{group.title}</div>
                            <div className="cd-grid">
                                {group.fields.map(([key, label]) => (
                                    <CopyField
                                        key={key}
                                        label={label}
                                        value={key === 'clickTime'
                                          ? formatUtcDateTime(data[key], { includeYear: true })
                                          : data[key]}
                                        wide={key === 'finalUrl' || key === 'userAgent' || key === 'naming'}
                                    />
                                ))}
                            </div>
                        </section>
                    ))
                )}

                {repeats.length ? (
                    <section className="cd-group">
                        <div className="cd-group-title">Повторні кліки · {repeats.length}</div>
                        <div className="cd-repeats">
                            {repeats.map((item) => {
                                const isOpen = openRepeat === item.id;
                                const payload = item.payload || {};

                                return (
                                    <div className={`cd-repeat${isOpen ? ' is-open' : ''}`} key={item.id}>
                                        <button
                                            type="button"
                                            className="cd-repeat-head"
                                            onClick={() => setOpenRepeat(isOpen ? null : item.id)}
                                        >
                                            <span className={`click-log-dot is-${logTone(item.clickLog)}`} />
                                            <span className="cd-repeat-time">
                                                {formatUtcDateTime(item.clickTime, { includeYear: true })}
                                            </span>
                                            <span className="cd-repeat-log">{logLabel(item.clickLog)}</span>
                                            <span className="cd-repeat-ip">{payload.ip || '—'}</span>
                                        </button>

                                        {isOpen ? (
                                            <div className="cd-grid cd-repeat-body">
                                                {repeatFields.map(([key, label]) => (
                                                    <CopyField
                                                        key={key}
                                                        label={label}
                                                        value={payload[key]}
                                                        wide={key === 'userAgent' || key === 'naming' || key === 'referer'}
                                                    />
                                                ))}
                                                <CopyField label="Final URL" value={item.finalUrl} wide />
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                ) : null}
            </BsModal.Body>
        </BsModal>
    );
}
